import React from "react";

import { faSpinnerThird } from "@fortawesome/pro-regular-svg-icons/faSpinnerThird";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import notification from "antd/es/notification";
import { push } from "connected-react-router";
import { type MessageDescriptor } from "react-intl";
import { all, call, put, select, take, takeEvery, takeLatest } from "redux-saga/effects";
import { getType } from "typesafe-actions";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import OrganizationSetting from "@mapmycustomers/shared/enum/OrganizationSetting";
import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import { RouteTravelMode } from "@mapmycustomers/shared/enum/routes/RouteTravelMode";
import { RouteType } from "@mapmycustomers/shared/enum/routes/RouteType";
import Address from "@mapmycustomers/shared/types/Address";
import { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import { Activity, Company, Person } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import Route, {
  CreateRoutePayload,
  CreateRouteStopItem,
  PreviewRoutePayload,
  RoutePreview,
  RoutePreviewMetrics,
  RouteStopBulkItem,
} from "@mapmycustomers/shared/types/entity/Route";
import { RouteEdgeEntity } from "@mapmycustomers/shared/types/entity/RouteDetail";
import UpdateRoutePayload from "@mapmycustomers/shared/types/entity/UpdateRoutePayload";
import Organization from "@mapmycustomers/shared/types/Organization";
import { GeoPoint } from "@mapmycustomers/shared/types/shapes";
import User, { UserRef } from "@mapmycustomers/shared/types/User";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import i18nService from "@app/config/I18nService";
import Path from "@app/enum/Path";
import RouteSubPath from "@app/enum/RouteSubPath";
import { buildRoutePreviewMetrics } from "@app/scene/routes/utils/buildRouteLegMetrics";
import { getActivityTypes } from "@app/store/activity";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import {
  doesOrganizationSupportAdvancedRouting,
  getCurrentUserId,
  getOrganizationId,
  getOrganizationSettingValue,
} from "@app/store/iam";
import { routeValidationErrorMessages } from "@app/store/routes/messages";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import { findDifference } from "@app/util/arrayUtils";
import { MAX_ROUTE_STOPS } from "@app/util/consts";
import { stripPinFields } from "@app/util/route/buildPreviewPayloadFromRoute";
import isRouteComplete from "@app/util/route/isRouteComplete";
import getRoutePreviewMetrics from "@app/util/route/routePreviewMetrics";
import { getScheduledActivitiesWindow } from "@app/util/route/routeStartTime";

import {
  addManualStop,
  buildAndSaveRoute,
  buildRouteFromEditor,
  createRoute,
  fetchScheduledActivities,
  insertStop,
  invertStops,
  reorderStop,
  saveRoute,
  updateDetails,
  updateEndAddress,
  updateEndEntity,
  updateRouteStops,
  updateStartAddress,
  updateStartEntity,
} from "../actions";
import { messages } from "../messages";
import sagaStyles from "../saga.module.scss";
import { EditorRouteStop } from "../types/RouteStop";

import {
  getEditorRoutePreview,
  getOriginalRoute,
  getRouteEditorAllottedTime,
  getRouteEditorEndAddress,
  getRouteEditorEndEntity,
  getRouteEditorEndEntityId,
  getRouteEditorEndEntityType,
  getRouteEditorEndGeoPoint,
  getRouteEditorIsPreviewStale,
  getRouteEditorName,
  getRouteEditorOwner,
  getRouteEditorRouteType,
  getRouteEditorScheduledActivities,
  getRouteEditorSharedUserIds,
  getRouteEditorStartAddress,
  getRouteEditorStartAt,
  getRouteEditorStartEntity,
  getRouteEditorStartEntityId,
  getRouteEditorStartEntityType,
  getRouteEditorStartGeoPoint,
  getRouteEditorStops,
  getRouteEditorTravelMode,
} from "./selectors";
import {
  buildEndLocationPayload,
  buildStartLocationPayload,
} from "./utils/buildEdgeLocationPayload";
import buildStopsDiff from "./utils/buildStopsDiff";
import getAppendStopsOperations from "./utils/getAppendStopsOperations";

const BUILD_NOTIFICATION_KEY = "routeEditorBuild";

const CREATE_NOTIFICATION_KEY = "routeEditorCreate";

const collectCreateStopItems = (
  activities: ReadonlyArray<Activity>,
  stops: ReadonlyArray<EditorRouteStop>
): CreateRouteStopItem[] =>
  getAppendStopsOperations(activities, stops)
    .filter((op): op is Extract<RouteStopBulkItem, { _action_: "c" }> => op._action_ === "c")
    .map(({ _action_, ...rest }, index) => {
      void _action_;
      return { ...rest, displayOrder: index };
    });

const notifyError = (
  descriptor: MessageDescriptor,
  values?: Parameters<typeof i18nService.formatMessage>[2]
) =>
  notification.error({
    message: i18nService.formatMessage(descriptor, String(descriptor.defaultMessage ?? ""), values),
  });

export function* onUpdateStartAddress({
  payload: { address, geoPoint },
}: ReturnType<typeof updateStartAddress.request>) {
  try {
    if (!address || geoPoint) {
      yield put(updateStartAddress.success({ address, geoPoint }));
      return;
    }
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const result: GeocodeResult = yield callApi("geocodeAddress", orgId, address);
    yield put(updateStartAddress.success({ address, geoPoint: result?.geoPoint ?? undefined }));
  } catch (error) {
    yield put(updateStartAddress.failure());
    yield put(handleError({ error }));
  }
}

export function* onUpdateEndAddress({
  payload: { address, geoPoint },
}: ReturnType<typeof updateEndAddress.request>) {
  try {
    if (!address || geoPoint) {
      yield put(updateEndAddress.success({ address, geoPoint }));
      return;
    }
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const result: GeocodeResult = yield callApi("geocodeAddress", orgId, address);
    yield put(updateEndAddress.success({ address, geoPoint: result?.geoPoint ?? undefined }));
  } catch (error) {
    yield put(updateEndAddress.failure());
    yield put(handleError({ error }));
  }
}

export function* onUpdateRouteStops({ payload }: ReturnType<typeof updateRouteStops.request>) {
  const { addEntityIds, entityType, removeEntityIds } = payload;
  try {
    if (!addEntityIds || addEntityIds.length === 0) {
      yield put(updateRouteStops.success({ entityType, removeEntityIds }));
      return;
    }
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const apiMethod = entityType === EntityType.COMPANY ? "fetchCompanies" : "fetchPeople";
    const response: ListResponse<Company | Person> = yield callApi(apiMethod, orgId, {
      $filters: { $and: [{ id: { $in: addEntityIds } }] },
    });
    yield put(
      updateRouteStops.success({
        addEntities: response.data,
        entityType,
        removeEntityIds,
      })
    );
  } catch (error) {
    yield put(updateRouteStops.failure());
    yield put(handleError({ error }));
  }
}

export function* onInsertStop({ payload }: ReturnType<typeof insertStop.request>) {
  const { entityId, entityType, insertAt, positional } = payload;
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const apiMethod = entityType === EntityType.COMPANY ? "fetchCompanies" : "fetchPeople";
    const response: ListResponse<Company | Person> = yield callApi(apiMethod, orgId, {
      $filters: { $and: [{ id: { $in: [entityId] } }] },
    });
    const entity = response.data[0];
    if (!entity) {
      yield put(insertStop.failure());
      return;
    }
    yield put(insertStop.success({ entity, entityType, insertAt, positional }));
  } catch (error) {
    yield put(insertStop.failure());
    yield put(handleError({ error }));
  }
}

const toRouteEdgeEntity = ({
  id,
  address,
  entity,
  geoPoint,
  name,
}: Company | Person): RouteEdgeEntity => ({
  id,
  address,
  entity,
  geoPoint,
  name,
});

function* fetchRouteEdgeEntity(payload: {
  entityId: RouteEdgeEntity["id"];
  entityType: RouteEdgeEntity["entity"];
}): Generator<unknown, Company | Person | undefined, never> {
  const orgId: Organization["id"] = yield select(getOrganizationId);
  const apiMethod = payload.entityType === EntityType.COMPANY ? "fetchCompanies" : "fetchPeople";
  const response: ListResponse<Company | Person> = yield callApi(apiMethod, orgId, {
    $filters: { $and: [{ id: { $in: [payload.entityId] } }] },
  });
  return response.data[0];
}

export function* onUpdateStartEntity({ payload }: ReturnType<typeof updateStartEntity.request>) {
  try {
    const entity: Company | Person | undefined = yield call(fetchRouteEdgeEntity, payload);
    if (!entity) {
      yield put(updateStartEntity.failure());
      return;
    }
    yield put(updateStartEntity.success({ entity: toRouteEdgeEntity(entity) }));
  } catch (error) {
    yield put(updateStartEntity.failure());
    yield put(handleError({ error }));
  }
}

export function* onUpdateEndEntity({ payload }: ReturnType<typeof updateEndEntity.request>) {
  try {
    const entity: Company | Person | undefined = yield call(fetchRouteEdgeEntity, payload);
    if (!entity) {
      yield put(updateEndEntity.failure());
      return;
    }
    yield put(updateEndEntity.success({ entity: toRouteEdgeEntity(entity) }));
  } catch (error) {
    yield put(updateEndEntity.failure());
    yield put(handleError({ error }));
  }
}

// Any manual ordering of stops — drag-to-reorder, Invert stops, or inserting one into a chosen
// slot (switchToOrderedOnPositionalInsert) — makes the route's optimized order no longer
// meaningful, so demote OPTIMIZED routes to ORDERED and tell the user their custom order will
// now be used. No-op when already ORDERED.
export function* switchToOrderedOnManualReorder() {
  const currentType: RouteType | undefined = yield select(getRouteEditorRouteType);
  if (currentType !== RouteType.OPTIMIZED) {
    return;
  }

  notification.info({
    message: i18nService.formatMessage(
      messages.routeTypeChangedToOrdered,
      String(messages.routeTypeChangedToOrdered.defaultMessage ?? "")
    ),
  });

  yield put(updateDetails({ routeType: RouteType.ORDERED }));
}

// Inserting a stop into a slot the user picked is as much a manual ordering act as dragging one
// there, so it demotes the same way: an OPTIMIZED route would otherwise re-optimize on the next
// build and move the stop straight back out of that slot, silently discarding the choice the "+"
// gap just offered (ENG-1229). Appends carry no chosen slot and are left alone — see the
// `positional` comment on insertStop.
export function* switchToOrderedOnPositionalInsert({
  payload,
}: ReturnType<typeof addManualStop> | ReturnType<typeof insertStop.success>) {
  if (!payload.positional) {
    return;
  }
  yield call(switchToOrderedOnManualReorder);
}

export function* onFetchScheduledActivities() {
  try {
    const startAt: Date | undefined = yield select(getRouteEditorStartAt);
    if (!startAt) {
      yield put(fetchScheduledActivities.success([]));
      return;
    }
    const advancedRouting: boolean = yield select(doesOrganizationSupportAdvancedRouting);
    if (!advancedRouting) {
      yield put(fetchScheduledActivities.success([]));
      return;
    }
    const configuredTypeIds: ActivityType["id"][] | undefined = yield select(
      getOrganizationSettingValue,
      OrganizationSetting.NEW_ROUTE_SUGGESTION_ACTIVITY_TYPE
    );
    const activityTypes: ActivityType[] = yield select(getActivityTypes);
    const typeIds = activityTypes
      .filter(
        ({ id, active }) =>
          active && (configuredTypeIds?.includes(id) || !configuredTypeIds?.length)
      )
      .map(({ id }) => id);

    if (typeIds.length === 0) {
      yield put(fetchScheduledActivities.success([]));
      return;
    }

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const currentUserId: User["id"] = yield select(getCurrentUserId);

    // Window = [max(startAt, now), endOfDay(startAt)] — see getScheduledActivitiesWindow.
    // Absolute ISO datetimes compare absolutely (no $timezone needed); the upper bound
    // stops later days leaking. call() keeps `now` injectable for saga tests.
    const { from: scheduledFrom, to: scheduledTo }: { from: Date; to: Date } = yield call(
      getScheduledActivitiesWindow,
      startAt
    );

    const response: ListResponse<Activity> = yield callApi("fetchActivities", orgId, {
      $filters: {
        $and: [
          { crmActivityTypeId: { $in: typeIds } },
          { assigneeId: currentUserId },
          { completed: false },
          {
            startAt: {
              [PlatformFilterOperator.GREATER_THAN_OR_EQUAL]: scheduledFrom.toISOString(),
              [PlatformFilterOperator.LESS_THAN_OR_EQUAL]: scheduledTo.toISOString(),
            },
          },
          {
            $or: [
              { "account.geoPoint": { [PlatformFilterOperator.NOT_EQUAL]: null } },
              // TODO(TART-27164): contact-only activities can't go into a
              // Company-Route payload. Revisit when People routes are wired.
              { "contact.geoPoint": { [PlatformFilterOperator.NOT_EQUAL]: null } },
            ],
          },
        ],
      },
    });
    yield put(fetchScheduledActivities.success(response.data));
  } catch (error) {
    yield put(fetchScheduledActivities.failure());
    yield put(handleError({ error }));
  }
}

export function* validateRouteShowNotificationsAndReturnResult(): Generator<
  unknown,
  boolean,
  unknown
> {
  const startEntityType = (yield select(getRouteEditorStartEntityType)) as
    | RouteEdgeEntity["entity"]
    | undefined;
  const startAddress = (yield select(getRouteEditorStartAddress)) as Address | undefined;
  const startGeoPoint = (yield select(getRouteEditorStartGeoPoint)) as GeoPoint | undefined;
  if (startEntityType) {
    const startEntity = (yield select(getRouteEditorStartEntity)) as RouteEdgeEntity | undefined;
    const startEntityId = (yield select(getRouteEditorStartEntityId)) as
      | RouteEdgeEntity["id"]
      | undefined;
    const hasBackup = startEntityId != null && !!startAddress && !!startGeoPoint;
    if (!startEntity && !hasBackup) {
      notifyError(messages.buildErrorNoStartEntity);
      return false;
    }
  } else {
    if (!startAddress) {
      notifyError(messages.buildErrorNoStartAddress);
      return false;
    }
    if (!startGeoPoint) {
      notifyError(messages.buildErrorNoStartGeoPoint);
      return false;
    }
  }

  const endEntityType = (yield select(getRouteEditorEndEntityType)) as
    | RouteEdgeEntity["entity"]
    | undefined;
  const endAddress = (yield select(getRouteEditorEndAddress)) as Address | undefined;
  const endGeoPoint = (yield select(getRouteEditorEndGeoPoint)) as GeoPoint | undefined;
  if (endEntityType) {
    const endEntity = (yield select(getRouteEditorEndEntity)) as RouteEdgeEntity | undefined;
    const endEntityId = (yield select(getRouteEditorEndEntityId)) as
      | RouteEdgeEntity["id"]
      | undefined;
    const hasBackup = endEntityId != null && !!endAddress && !!endGeoPoint;
    if (!endEntity && !hasBackup) {
      notifyError(messages.buildErrorNoEndEntity);
      return false;
    }
  } else if (endAddress && !endGeoPoint) {
    notifyError(messages.buildErrorNoEndGeoPoint);
    return false;
  }

  const activities: ReadonlyArray<Activity> = (yield select(
    getRouteEditorScheduledActivities
  )) as ReadonlyArray<Activity>;
  const stops: ReadonlyArray<EditorRouteStop> = (yield select(
    getRouteEditorStops
  )) as ReadonlyArray<EditorRouteStop>;
  const total = activities.length + stops.length;
  if (total < 1) {
    notifyError(messages.buildErrorNoStops);
    return false;
  }
  if (total > MAX_ROUTE_STOPS) {
    notifyError(messages.buildErrorTooManyStops, { count: total, max: MAX_ROUTE_STOPS });
    return false;
  }

  return true;
}

export function* onBuildRouteFromEditor() {
  const isValid: boolean = yield call(validateRouteShowNotificationsAndReturnResult);
  if (!isValid) {
    yield put(buildRouteFromEditor.failure());
    return;
  }

  const startAddress: Address | undefined = yield select(getRouteEditorStartAddress);
  const startGeoPoint: GeoPoint | undefined = yield select(getRouteEditorStartGeoPoint);
  const startEntity: RouteEdgeEntity | undefined = yield select(getRouteEditorStartEntity);
  const endAddress: Address | undefined = yield select(getRouteEditorEndAddress);
  const endGeoPoint: GeoPoint | undefined = yield select(getRouteEditorEndGeoPoint);
  const endEntity: RouteEdgeEntity | undefined = yield select(getRouteEditorEndEntity);
  const travelMode: RouteTravelMode | undefined = yield select(getRouteEditorTravelMode);
  const activities: ReadonlyArray<Activity> = yield select(getRouteEditorScheduledActivities);
  const stops: ReadonlyArray<EditorRouteStop> = yield select(getRouteEditorStops);
  const allottedTime: number = yield select(getRouteEditorAllottedTime);

  notification.info({
    icon: <FontAwesomeIcon className={sagaStyles.notificationLoader} icon={faSpinnerThird} spin />,
    key: BUILD_NOTIFICATION_KEY,
    message: i18nService.formatMessage(
      messages.buildPending,
      String(messages.buildPending.defaultMessage ?? "")
    ),
  });

  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const routeType: RouteType | undefined = yield select(getRouteEditorRouteType);
    const startAt: Date | undefined = yield select(getRouteEditorStartAt);
    if (!startAt) {
      notifyError(messages.buildErrorNoStartAt);
      yield put(buildRouteFromEditor.failure());
      return;
    }
    const startLocation = buildStartLocationPayload({
      address: startAddress,
      entity: startEntity,
      geoPoint: startGeoPoint,
    });
    const endLocation = buildEndLocationPayload({
      address: endAddress,
      entity: endEntity,
      geoPoint: endGeoPoint,
    });
    // Nothing remains, so the drive worth pricing is the whole one. Ordered and unpinned:
    // re-optimizing would move visited stops, and the save would persist that over what was driven.
    const complete = isRouteComplete(stops);
    const stopItems = collectCreateStopItems(
      activities,
      complete ? stops : stops.filter((stop) => !stop.checkedOff)
    );
    const payload: PreviewRoutePayload = {
      routeDetail: {
        allottedTime,
        startAt: startAt.toISOString(),
        travelMode: travelMode!,
        type: complete ? RouteType.ORDERED : (routeType ?? RouteType.OPTIMIZED),
        ...startLocation!,
        ...endLocation,
      },
      stopTime: complete ? stopItems.map(stripPinFields) : stopItems,
    };
    const preview: RoutePreview = yield callApi("previewUnifiedRoute", orgId, payload);
    yield put(buildRouteFromEditor.success(preview));
  } catch (error) {
    yield put(buildRouteFromEditor.failure());
    yield put(handleError({ defaultMessage: messages.buildErrorGeneric, error }));
  } finally {
    notification.close(BUILD_NOTIFICATION_KEY);
  }
}

export function* onCreateRoute({ payload }: ReturnType<typeof createRoute.request>) {
  const isValid: boolean = yield call(validateRouteShowNotificationsAndReturnResult);
  if (!isValid) {
    yield put(createRoute.failure());
    return;
  }

  const name: Route["name"] | undefined = yield select(getRouteEditorName);
  const owner: undefined | UserRef = yield select(getRouteEditorOwner);
  const startAt: Date | undefined = yield select(getRouteEditorStartAt);
  if (!startAt) {
    notifyError(messages.buildErrorNoStartAt);
    yield put(createRoute.failure());
    return;
  }
  const startAddress: Address | undefined = yield select(getRouteEditorStartAddress);
  const startGeoPoint: GeoPoint | undefined = yield select(getRouteEditorStartGeoPoint);
  const startEntity: RouteEdgeEntity | undefined = yield select(getRouteEditorStartEntity);
  const endAddress: Address | undefined = yield select(getRouteEditorEndAddress);
  const endGeoPoint: GeoPoint | undefined = yield select(getRouteEditorEndGeoPoint);
  const endEntity: RouteEdgeEntity | undefined = yield select(getRouteEditorEndEntity);
  const travelMode: RouteTravelMode | undefined = yield select(getRouteEditorTravelMode);
  const routeType: RouteType | undefined = yield select(getRouteEditorRouteType);
  const activities: ReadonlyArray<Activity> = yield select(getRouteEditorScheduledActivities);
  const stops: ReadonlyArray<EditorRouteStop> = yield select(getRouteEditorStops);
  const allottedTime: number = yield select(getRouteEditorAllottedTime);
  const orgId: Organization["id"] = yield select(getOrganizationId);
  const editorPreview: RoutePreview | undefined = yield select(getEditorRoutePreview);

  notification.info({
    icon: <FontAwesomeIcon className={sagaStyles.notificationLoader} icon={faSpinnerThird} spin />,
    key: CREATE_NOTIFICATION_KEY,
    message: i18nService.formatMessage(
      messages.createPending,
      String(messages.createPending.defaultMessage ?? "")
    ),
  });

  try {
    const startLocation = buildStartLocationPayload({
      address: startAddress,
      entity: startEntity,
      geoPoint: startGeoPoint,
    });
    const endLocation = buildEndLocationPayload({
      address: endAddress,
      entity: endEntity,
      geoPoint: endGeoPoint,
    });
    const stopItems = getAppendStopsOperations(activities, stops)
      .filter((op): op is Extract<RouteStopBulkItem, { _action_: "c" }> => op._action_ === "c")
      .map(({ _action_, ...rest }) => {
        void _action_;
        return rest;
      });

    const createPayload: CreateRoutePayload = {
      name: name ?? "",
      routeDetail: {
        allottedTime,
        startAt: startAt.toISOString(),
        travelMode: travelMode!,
        type: routeType ?? RouteType.OPTIMIZED,
        ...startLocation!,
        ...endLocation,
        ...(editorPreview ? getRoutePreviewMetrics(editorPreview) : {}),
      },
      ...(stopItems.length > 0 ? { stopTime: stopItems } : {}),
      // Assigns ownership at creation (defaults to the caller when absent) — the
      // Owner picker's choice was previously never sent and silently lost.
      ...(owner ? { user: { id: owner.id } } : {}),
    };

    const createdRoute: Route = yield callApi("createUnifiedRoute", orgId, createPayload);

    const sharedUserIds: User["id"][] = yield select(getRouteEditorSharedUserIds);
    const sharees = sharedUserIds.filter((id) => id !== owner?.id);
    if (sharees.length > 0) {
      yield callApi(
        "bulkCreateRouteShare",
        orgId,
        sharees.map((userId) => ({ routeId: createdRoute.id, userId }))
      );
    }

    notification.success({
      message: i18nService.formatMessage(
        messages.createSuccess,
        String(messages.createSuccess.defaultMessage ?? "")
      ),
    });
    yield put(createRoute.success({ route: createdRoute }));
    yield put(notifyAboutChanges({ added: [createdRoute], entityType: EntityType.ROUTE }));
    if (!payload?.skipRedirect) {
      yield put(push(`${Path.ROUTE}/${RouteSubPath.VIEW}/${createdRoute.id}`));
    }
  } catch (error) {
    yield put(createRoute.failure());
    yield put(
      handleError({
        defaultMessage: messages.createFailure,
        error,
        messageByErrorCodeMap: routeValidationErrorMessages,
      })
    );
  } finally {
    notification.close(CREATE_NOTIFICATION_KEY);
  }
}

export function* onSaveRoute({ payload }: ReturnType<typeof saveRoute.request>) {
  const isValid: boolean = yield call(validateRouteShowNotificationsAndReturnResult);
  if (!isValid) {
    yield put(saveRoute.failure());
    return;
  }

  const originalRoute: Route | undefined = yield select(getOriginalRoute);
  if (!originalRoute) {
    yield put(saveRoute.failure());
    return;
  }

  const orgId: Organization["id"] = yield select(getOrganizationId);

  const name: Route["name"] | undefined = yield select(getRouteEditorName);
  const owner: undefined | UserRef = yield select(getRouteEditorOwner);
  const startAt: Date | undefined = yield select(getRouteEditorStartAt);
  if (!startAt) {
    notifyError(messages.buildErrorNoStartAt);
    yield put(saveRoute.failure());
    return;
  }
  const travelMode: RouteTravelMode | undefined = yield select(getRouteEditorTravelMode);
  const routeType: RouteType | undefined = yield select(getRouteEditorRouteType);
  const startAddress: Address | undefined = yield select(getRouteEditorStartAddress);
  const startGeoPoint: GeoPoint | undefined = yield select(getRouteEditorStartGeoPoint);
  const startEntity: RouteEdgeEntity | undefined = yield select(getRouteEditorStartEntity);
  const endAddress: Address | undefined = yield select(getRouteEditorEndAddress);
  const endGeoPoint: GeoPoint | undefined = yield select(getRouteEditorEndGeoPoint);
  const endEntity: RouteEdgeEntity | undefined = yield select(getRouteEditorEndEntity);
  const stops: ReadonlyArray<EditorRouteStop> = yield select(getRouteEditorStops);
  const allottedTime: number = yield select(getRouteEditorAllottedTime);
  const editorPreview: RoutePreview | undefined = yield select(getEditorRoutePreview);
  const metrics = editorPreview ? getRoutePreviewMetrics(editorPreview) : undefined;

  // Route-level metrics only; omitting `legMetrics` nulls the stale leg array. Never fall through
  // to no envelope: every buildStopsDiff update carries `displayOrder`, nulling all five columns.
  const buildSaveMetrics = (preview: RoutePreview): RoutePreviewMetrics =>
    isRouteComplete(stops)
      ? getRoutePreviewMetrics(preview)
      : buildRoutePreviewMetrics(stops, preview);

  try {
    const startLocation = buildStartLocationPayload({
      address: startAddress,
      entity: startEntity,
      geoPoint: startGeoPoint,
    });
    const endLocation = buildEndLocationPayload({
      address: endAddress,
      entity: endEntity,
      geoPoint: endGeoPoint,
    });
    const updatePayload: UpdateRoutePayload = {
      id: originalRoute.id,
      name,
      routeDetail: {
        id: originalRoute.routeDetail.id,
        allottedTime,
        startAt: startAt.toISOString(),
        travelMode: travelMode!,
        type: routeType,
        ...startLocation!,
        ...endLocation,
        ...(metrics ?? {}),
      },
      // Only reassign when the owner actually changed — a same-owner `user` write is
      // a needless reassignment request.
      ...(owner && owner.id !== originalRoute.user?.id ? { user: { id: owner.id } } : {}),
    };
    yield callApi("updateUnifiedRoute", orgId, originalRoute.id, updatePayload);

    const stopEdits: RouteStopBulkItem[] = buildStopsDiff(originalRoute, stops);
    const stopsMetrics = editorPreview ? buildSaveMetrics(editorPreview) : undefined;
    // The bulk envelope is the only write that can carry the leg array, and it has to follow the
    // PUT above, which always clears what was stored: the BE compares the inbound geoPoints by
    // value against raw column data they can never deep-equal, so every PUT reads as a
    // travel-basis change.
    //
    // So the envelope cannot ride only on there being stop edits. An edit that changes no stop —
    // a rename, an owner change — would otherwise leave the route with totals but no legs and
    // send the next open to a fresh preview. When there is nothing to diff, every stop is
    // restated as a bare no-op update purely to carry the metrics: the endpoint rejects an empty
    // `routeGroups` (v0160), and a bare update carries no geometry key, which keeps the batch
    // geometry-neutral so the BE does not read it as a stop change and clear the metrics it is
    // delivering. Ids come from the route we read, since one the BE cannot match fails the call.
    const metricsOnlyEdits: RouteStopBulkItem[] = stopsMetrics
      ? originalRoute.stopTime.map((stop) => ({ id: stop.id, _action_: "u" }))
      : [];
    const bulkEdits = stopEdits.length > 0 ? stopEdits : metricsOnlyEdits;
    if (bulkEdits.length > 0) {
      yield callApi(
        "bulkUpdateUnifiedRouteStops",
        orgId,
        originalRoute.id,
        bulkEdits,
        stopsMetrics
      );
    }

    const sharedUserIds: User["id"][] = yield select(getRouteEditorSharedUserIds);
    const [toAdd, toRemove] = findDifference(originalRoute.userIds, sharedUserIds);
    yield all([
      toAdd.length
        ? callApi(
            "bulkCreateRouteShare",
            orgId,
            toAdd.map((userId) => ({ routeId: originalRoute.id, userId }))
          )
        : undefined,
      toRemove.length
        ? callApi(
            "bulkDeleteRouteShare",
            orgId,
            toRemove.map((userId) => ({ routeId: originalRoute.id, userId }))
          )
        : undefined,
    ]);

    yield put(saveRoute.success({ route: originalRoute }));
    yield put(notifyAboutChanges({ entityType: EntityType.ROUTE, updatedIds: [originalRoute.id] }));
    if (!payload?.skipRedirect) {
      yield put(push(`${Path.ROUTE}/${RouteSubPath.VIEW}/${originalRoute.id}`));
    }
  } catch (error) {
    yield put(saveRoute.failure());
    yield put(
      handleError({
        defaultMessage: messages.updateFailure,
        error,
        messageByErrorCodeMap: routeValidationErrorMessages,
      })
    );
  }
}

export function* onBuildAndSaveRoute({ payload }: ReturnType<typeof buildAndSaveRoute>) {
  try {
    const isStale: boolean = yield select(getRouteEditorIsPreviewStale);
    const preview: RoutePreview | undefined = yield select(getEditorRoutePreview);
    if (isStale || !preview) {
      yield put(buildRouteFromEditor.request());
      const buildResult: { type: string } = yield take([
        getType(buildRouteFromEditor.success),
        getType(buildRouteFromEditor.failure),
      ]);
      if (buildResult.type === getType(buildRouteFromEditor.failure)) {
        payload.onFailure?.();
        return;
      }
    }

    const originalRoute: Route | undefined = yield select(getOriginalRoute);
    if (originalRoute) {
      yield put(saveRoute.request({ skipRedirect: true }));
      const result: { type: string } = yield take([
        getType(saveRoute.success),
        getType(saveRoute.failure),
      ]);
      if (result.type === getType(saveRoute.success)) {
        payload.onSuccess?.();
      } else {
        payload.onFailure?.();
      }
    } else {
      yield put(createRoute.request({ skipRedirect: true }));
      const result: { type: string } = yield take([
        getType(createRoute.success),
        getType(createRoute.failure),
      ]);
      if (result.type === getType(createRoute.success)) {
        payload.onSuccess?.();
      } else {
        payload.onFailure?.();
      }
    }
  } catch {
    payload.onFailure?.();
  }
}

export function* editorSaga() {
  yield all([
    takeLatest(updateRouteStops.request, onUpdateRouteStops),
    takeLatest(insertStop.request, onInsertStop),
    takeLatest(updateStartEntity.request, onUpdateStartEntity),
    takeLatest(updateEndEntity.request, onUpdateEndEntity),
    takeLatest(fetchScheduledActivities.request, onFetchScheduledActivities),
    takeLatest(updateStartAddress.request, onUpdateStartAddress),
    takeLatest(updateEndAddress.request, onUpdateEndAddress),
    takeEvery(reorderStop, switchToOrderedOnManualReorder),
    takeEvery(invertStops, switchToOrderedOnManualReorder),
    takeEvery([addManualStop, insertStop.success], switchToOrderedOnPositionalInsert),
    takeLatest(buildRouteFromEditor.request, onBuildRouteFromEditor),
    takeLatest(createRoute.request, onCreateRoute),
    takeLatest(saveRoute.request, onSaveRoute),
    takeLatest(buildAndSaveRoute, onBuildAndSaveRoute),
  ]);
}
